import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Search, X, Download, Bookmark, BookmarkPlus, Trash2, History } from 'lucide-react';
import { useHARStore } from '@/stores/har-store';
import { exportAsHAR, exportAsCSV, type FilterState } from '@har-insight/parser';
import { Dropdown, DropdownItem } from '@/components/Dropdown';
import { showToast } from '@/stores/toast-store';
import { cn } from '@/lib/utils';

const STATUS_OPTIONS = ['2xx', '3xx', '4xx', '5xx'];
const METHOD_OPTIONS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'OPTIONS', 'HEAD'];

// Storage key for filter presets
const PRESETS_STORAGE_KEY = 'har-insight-filter-presets';
// Storage key for search history
const SEARCH_HISTORY_KEY = 'har-insight-search-history';
const MAX_SEARCH_HISTORY = 10;

interface FilterPreset {
  id: string;
  name: string;
  filters: FilterState;
  createdAt: string;
}

interface SearchHistoryItem {
  id: string;
  query: string;
  isRegex: boolean;
  timestamp: number;
}

interface FilterChipProps {
  label: string;
  active: boolean;
  onClick: () => void;
}

function FilterChip({ label, active, onClick }: FilterChipProps) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'px-2 py-1 text-xs font-medium rounded transition-colors',
        active
          ? 'bg-brand-100 dark:bg-brand-500/20 text-brand-600 dark:text-brand-400 border border-brand-300 dark:border-brand-500/30'
          : 'bg-slate-100 dark:bg-slate-800/50 text-slate-600 dark:text-slate-400 border border-slate-300 dark:border-slate-700 hover:bg-slate-200 dark:hover:bg-slate-800'
      )}
    >
      {label}
    </button>
  );
}

export function FilterBar() {
  const { har, stats, filters, setFilters, clearFilters, filteredEntries } = useHARStore();
  
  // Preset management
  const [presets, setPresets] = useState<FilterPreset[]>(() => {
    if (typeof window === 'undefined') return [];
    try {
      const stored = localStorage.getItem(PRESETS_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [showPresetsDropdown, setShowPresetsDropdown] = useState(false);
  
  // Search history management
  const [searchHistory, setSearchHistory] = useState<SearchHistoryItem[]>(() => {
    if (typeof window === 'undefined') return [];
    try {
      const stored = localStorage.getItem(SEARCH_HISTORY_KEY);
      const history = stored ? JSON.parse(stored) : [];
      // Filter out empty queries and old entries (>30 days)
      const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
      return history.filter((item: SearchHistoryItem) => 
        item.query && item.query.trim() !== '' && item.timestamp > thirtyDaysAgo
      ).slice(0, MAX_SEARCH_HISTORY);
    } catch {
      return [];
    }
  });
  const [showHistoryDropdown, setShowHistoryDropdown] = useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  
  // Compute domains and methods directly from stats to avoid selector issues
  const domains = React.useMemo(() => {
    if (!stats) return [];
    return Object.keys(stats.byDomain).sort((a, b) => stats.byDomain[b] - stats.byDomain[a]);
  }, [stats]);
  
  const methods = React.useMemo(() => {
    if (!stats) return [];
    return Object.keys(stats.byMethod).sort();
  }, [stats]);

  const hasActiveFilters =
    filters.search ||
    filters.methods.length > 0 ||
    filters.statusCodes.length > 0 ||
    filters.domains.length > 0;

  // Save search to history when search changes
  useEffect(() => {
    if (filters.search && filters.search.trim() !== '') {
      const newItem: SearchHistoryItem = {
        id: `search-${Date.now()}`,
        query: filters.search,
        isRegex: filters.isRegex ?? false,
        timestamp: Date.now(),
      };
      
      setSearchHistory(prev => {
        // Remove duplicates
        const filtered = prev.filter(item => item.query !== filters.search);
        const updated = [newItem, ...filtered].slice(0, MAX_SEARCH_HISTORY);
        
        // Persist to localStorage
        if (typeof window !== 'undefined') {
          localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(updated));
        }
        
        return updated;
      });
    }
  }, [filters.search, filters.isRegex]);

  const toggleMethod = (method: string) => {
    const newMethods = filters.methods.includes(method)
      ? filters.methods.filter(m => m !== method)
      : [...filters.methods, method];
    setFilters({ methods: newMethods });
  };

  const toggleStatus = (status: string) => {
    const newStatuses = filters.statusCodes.includes(status)
      ? filters.statusCodes.filter(s => s !== status)
      : [...filters.statusCodes, status];
    setFilters({ statusCodes: newStatuses });
  };

  const toggleDomain = (domain: string) => {
    const newDomains = filters.domains.includes(domain)
      ? filters.domains.filter(d => d !== domain)
      : [...filters.domains, domain];
    setFilters({ domains: newDomains });
  };

  const handleExport = (format: 'har' | 'csv') => {
    if (!har) return;

    // Show progress toast for large exports
    const entryCount = filteredEntries.length;
    if (entryCount > 1000) {
      showToast(`Exporting ${entryCount.toLocaleString()} entries... This may take a moment.`, 'info', 5000);
    }

    const content = format === 'har'
      ? exportAsHAR(har, filteredEntries)
      : exportAsCSV(filteredEntries);

    const blob = new Blob([content], {
      type: format === 'har' ? 'application/json' : 'text/csv'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `export-${filteredEntries.length}-requests.${format}`;
    a.click();
    URL.revokeObjectURL(url);
    
    showToast(`Exported ${entryCount.toLocaleString()} requests as ${format.toUpperCase()}`, 'success');
  };

  // Save current filters as a preset
  const savePreset = useCallback(() => {
    if (!presetName.trim()) return;
    
    const newPreset: FilterPreset = {
      id: `preset-${Date.now()}`,
      name: presetName.trim(),
      filters: { ...filters },
      createdAt: new Date().toISOString(),
    };
    
    const updatedPresets = [...presets, newPreset];
    setPresets(updatedPresets);
    localStorage.setItem(PRESETS_STORAGE_KEY, JSON.stringify(updatedPresets));
    setPresetName('');
    setShowSaveDialog(false);
    showToast('Filter preset saved!', 'success');
  }, [presetName, filters, presets]);

  // Load a preset
  const loadPreset = useCallback((preset: FilterPreset) => {
    setFilters(preset.filters);
    setShowPresetsDropdown(false);
    showToast(`Loaded preset: ${preset.name}`, 'info');
  }, [setFilters]);

  // Delete a preset
  const deletePreset = useCallback((presetId: string) => {
    const updatedPresets = presets.filter(p => p.id !== presetId);
    setPresets(updatedPresets);
    localStorage.setItem(PRESETS_STORAGE_KEY, JSON.stringify(updatedPresets));
    showToast('Preset deleted', 'info');
  }, [presets]);

  // Load search from history
  const loadSearchFromHistory = useCallback((item: SearchHistoryItem) => {
    setFilters({ search: item.query, isRegex: item.isRegex });
    setShowHistoryDropdown(false);
    searchInputRef.current?.focus();
  }, [setFilters]);

  // Clear search history
  const clearSearchHistory = useCallback(() => {
    setSearchHistory([]);
    if (typeof window !== 'undefined') {
      localStorage.removeItem(SEARCH_HISTORY_KEY);
    }
    setShowHistoryDropdown(false);
    showToast('Search history cleared', 'info');
  }, []);

  // Generate preset name suggestion based on active filters
  const generatePresetName = useCallback(() => {
    const parts: string[] = [];
    if (filters.methods.length > 0) parts.push(filters.methods.join(','));
    if (filters.statusCodes.length > 0) parts.push(filters.statusCodes.join(','));
    if (filters.domains.length > 0) parts.push(`${filters.domains.length}domains`);
    if (filters.search) parts.push('search');
    
    return parts.length > 0 ? parts.join(' + ') : 'Custom Filter';
  }, [filters]);

  return (
    <div className="card p-4 space-y-4">
      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            ref={searchInputRef}
            type="text"
            placeholder={filters.isRegex ? "Filter by URL (regex)..." : "Filter by URL..."}
            value={filters.search}
            onChange={(e) => setFilters({ search: e.target.value })}
            onFocus={() => setIsSearchFocused(true)}
            onBlur={() => {
              // Delay to allow clicking dropdown items
              setTimeout(() => setIsSearchFocused(false), 200);
            }}
            className={cn("input w-full pl-10 pr-24", filters.isRegex && "font-mono")}
          />
          
          {/* Search history dropdown trigger */}
          {searchHistory.length > 0 && (
            <button
              onClick={() => setShowHistoryDropdown(!showHistoryDropdown)}
              onMouseDown={(e) => e.preventDefault()} // Prevent blur
              className={cn(
                "absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded transition-colors",
                showHistoryDropdown || isSearchFocused
                  ? "bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300"
                  : "text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
              )}
              title="Search history"
            >
              <History className="w-4 h-4" />
            </button>
          )}
          
          {/* Regex toggle - positioned to the left of history button */}
          <button
            onClick={() => setFilters({ isRegex: !filters.isRegex })}
            className={cn(
              "absolute right-9 top-1/2 -translate-y-1/2 px-1.5 py-0.5 text-xs font-mono rounded transition-colors",
              filters.isRegex
                ? "bg-brand-100 dark:bg-brand-500/20 text-brand-600 dark:text-brand-400"
                : "text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
            )}
            title="Toggle regex search"
          >
            .*
          </button>
          
          {/* Search history dropdown */}
          {showHistoryDropdown && searchHistory.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg shadow-lg z-50 overflow-hidden">
              <div className="px-3 py-2 text-xs font-medium text-slate-500 uppercase tracking-wider border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
                <span>Recent Searches</span>
                <button
                  onClick={clearSearchHistory}
                  className="text-slate-400 hover:text-red-500 transition-colors"
                  title="Clear history"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="max-h-48 overflow-y-auto">
                {searchHistory.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => loadSearchFromHistory(item)}
                    className="w-full text-left px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors flex items-center gap-2 group"
                  >
                    <Search className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                    <span className="text-sm text-slate-700 dark:text-slate-300 flex-1 truncate font-mono text-xs">
                      {item.query}
                    </span>
                    {item.isRegex && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-brand-100 dark:bg-brand-500/20 text-brand-600 dark:text-brand-400 rounded">
                        regex
                      </span>
                    )}
                    <span className="text-xs text-slate-400">
                      {new Date(item.timestamp).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        
        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="btn-ghost flex items-center gap-2 text-sm"
          >
            <X className="w-4 h-4" />
            Clear
          </button>
        )}

        {/* Presets dropdown */}
        <div className="relative">
          <Dropdown
            trigger={
              <button 
                className="btn-ghost flex items-center gap-2 text-sm"
                onClick={() => setShowPresetsDropdown(!showPresetsDropdown)}
              >
                <Bookmark className="w-4 h-4" />
                <span className="hidden sm:inline">Presets</span>
                {presets.length > 0 && (
                  <span className="ml-1 text-xs bg-slate-200 dark:bg-slate-800 px-1.5 py-0.5 rounded-full">
                    {presets.length}
                  </span>
                )}
              </button>
            }
          >
            {presets.length === 0 ? (
              <div className="px-4 py-3 text-sm text-slate-500">
                No saved presets yet
              </div>
            ) : (
              <>
                {presets.map((preset) => (
                  <div key={preset.id} className="flex items-center group">
                    <DropdownItem 
                      onClick={() => loadPreset(preset)}
                      className="flex-1"
                    >
                      <div className="flex flex-col">
                        <span className="font-medium">{preset.name}</span>
                        <span className="text-xs text-slate-500">
                          {preset.filters.methods.length > 0 && `${preset.filters.methods.length} methods `}
                          {preset.filters.statusCodes.length > 0 && `${preset.filters.statusCodes.length} statuses `}
                          {preset.filters.domains.length > 0 && `${preset.filters.domains.length} domains`}
                        </span>
                      </div>
                    </DropdownItem>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deletePreset(preset.id);
                      }}
                      className="p-2 text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Delete preset"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </>
            )}
          </Dropdown>
        </div>

        {/* Export dropdown */}
        <Dropdown
          trigger={
            <button className="btn-ghost flex items-center gap-2 text-sm">
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Export</span>
            </button>
          }
        >
          <DropdownItem onClick={() => handleExport('har')}>
            Export as HAR
          </DropdownItem>
          <DropdownItem onClick={() => handleExport('csv')}>
            Export as CSV
          </DropdownItem>
        </Dropdown>
      </div>

      {/* Filter chips */}
      <div className="flex flex-wrap items-center gap-4">
        {/* Methods */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-400 dark:text-slate-500 uppercase tracking-wider">Method:</span>
          <div className="flex flex-wrap gap-1">
            {METHOD_OPTIONS.filter(m => methods.includes(m)).map(method => (
              <FilterChip
                key={method}
                label={method}
                active={filters.methods.includes(method)}
                onClick={() => toggleMethod(method)}
              />
            ))}
          </div>
        </div>

        {/* Status codes */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-400 dark:text-slate-500 uppercase tracking-wider">Status:</span>
          <div className="flex flex-wrap gap-1">
            {STATUS_OPTIONS.map(status => (
              <FilterChip
                key={status}
                label={status}
                active={filters.statusCodes.includes(status)}
                onClick={() => toggleStatus(status)}
              />
            ))}
          </div>
        </div>

        {/* Domains (show top 5) */}
        {domains.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400 dark:text-slate-500 uppercase tracking-wider">Domain:</span>
            <div className="flex flex-wrap gap-1">
              {domains.slice(0, 5).map(domain => (
                <FilterChip
                  key={domain}
                  label={domain}
                  active={filters.domains.includes(domain)}
                  onClick={() => toggleDomain(domain)}
                />
              ))}
              {domains.length > 5 && (
                <span className="text-xs text-slate-400 dark:text-slate-600 px-1 py-1">
                  +{domains.length - 5} more
                </span>
              )}
            </div>
          </div>
        )}

        {/* Save preset button (only show when filters are active) */}
        {hasActiveFilters && (
          <button
            onClick={() => {
              setPresetName(generatePresetName());
              setShowSaveDialog(true);
            }}
            className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-brand-600 dark:text-brand-400 bg-brand-50 dark:bg-brand-500/10 border border-brand-200 dark:border-brand-500/30 rounded hover:bg-brand-100 dark:hover:bg-brand-500/20 transition-colors"
          >
            <BookmarkPlus className="w-3.5 h-3.5" />
            Save Preset
          </button>
        )}
      </div>

      {/* Save Preset Dialog */}
      {showSaveDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-900 rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
              Save Filter Preset
            </h3>
            <p className="text-sm text-slate-500 mb-4">
              Save your current filter configuration for quick access later.
            </p>
            <input
              type="text"
              value={presetName}
              onChange={(e) => setPresetName(e.target.value)}
              placeholder="Preset name..."
              className="input w-full mb-4"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') savePreset();
                if (e.key === 'Escape') setShowSaveDialog(false);
              }}
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowSaveDialog(false)}
                className="btn-ghost text-sm"
              >
                Cancel
              </button>
              <button
                onClick={savePreset}
                disabled={!presetName.trim()}
                className="btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Save Preset
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
